import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { AllDeal, AgreementApiModel, DealOverview } from '../models/DealModel';
import { accountSelector } from '../store/shared/accountSlice';
import {
  useGetDealsListQuery,
  useGetDealsOverviewQuery,
} from '../store/shared/dealsApiSlice';
import {
  setHomeDeal,
  resetHomeDeal,
  selectedHomeDealSelector,
} from '../store/shared/selectedHomeDealSlice';

interface IGetDealsAPI {
  data: AgreementApiModel[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  selectedHomeDeal: AgreementApiModel;
  setSelectedHomeDeal: (deal: AgreementApiModel) => void;
  resetSelectedHomeDeal: () => void;
  refetch: () => void;
}

export interface IUseGetDeals {
  allDealsEnabled?: boolean;
  overviewOnly?: boolean;
}

const useGetDeals = (
  { allDealsEnabled = false, overviewOnly = false }: IUseGetDeals = {
    allDealsEnabled: false,
    overviewOnly: false,
  },
): IGetDealsAPI => {
  const { dealId } = useParams<{ dealId: string }>();
  const { currentAccountId } = useSelector(accountSelector);
  const selectedHomeDeal = useSelector(selectedHomeDealSelector);
  const dispatch = useDispatch();
  const { data, isLoading, isFetching, isError, refetch } = overviewOnly
    ? useGetDealsOverviewQuery(currentAccountId, { skip: !currentAccountId })
    : useGetDealsListQuery(currentAccountId, { skip: !currentAccountId });

  const setSelectedHomeDeal = (deal: AgreementApiModel | DealOverview) => {
    dispatch(setHomeDeal(deal));
  };

  const resetSelectedHomeDeal = () => {
    dispatch(resetHomeDeal());
  };

  useEffect(() => {
    const deals: [] = data as [];

    if (
      data &&
      !isLoading &&
      !isFetching &&
      allDealsEnabled &&
      !dealId &&
      !selectedHomeDeal.homeDealId
    ) {
      setSelectedHomeDeal(AllDeal);
    } else if (
      deals &&
      !isLoading &&
      !isFetching &&
      allDealsEnabled &&
      dealId === AllDeal.id &&
      selectedHomeDeal.homeDealId === AllDeal.id
    ) {
      setSelectedHomeDeal(AllDeal);
    } else if (deals && !isLoading && !isFetching && dealId) {
      const deal =
        deals.find((d: DealOverview | AgreementApiModel) => d.id === dealId) ??
        data[data.length - 1];
      setSelectedHomeDeal(deal);
    } else if (
      deals &&
      !isLoading &&
      !isFetching &&
      selectedHomeDeal.homeDealId &&
      (!allDealsEnabled ||
        (allDealsEnabled && selectedHomeDeal.homeDealId !== AllDeal.id))
    ) {
      const deal = deals.find(
        (d: DealOverview | AgreementApiModel) =>
          d.id === selectedHomeDeal.homeDealId,
      );
      if (!deal) setSelectedHomeDeal(data[data.length - 1]);
      else setSelectedHomeDeal(deal);
    } else if (
      data &&
      !isLoading &&
      !isFetching &&
      !selectedHomeDeal.homeDealId &&
      !dealId
    ) {
      // USE LAST DEAL INSTEAD OF FIRST DEAL
      // Should REFACTOR once API RESPONSE will be sorted from newest to oldest
      setSelectedHomeDeal(data[data.length - 1]);
    }
  }, [data, isLoading, isFetching]);

  useEffect(() => {
    if (selectedHomeDeal.homeDeal && !data?.length) {
      refetch();
    }
  }, [selectedHomeDeal.homeDeal]);

  return {
    data: data || [],
    isLoading,
    isFetching,
    isError,
    selectedHomeDeal: selectedHomeDeal.homeDeal,
    setSelectedHomeDeal,
    resetSelectedHomeDeal,
    refetch,
  };
};

export default useGetDeals;
